import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import HomepageActivitiesSection from "@/components/HomepageActivitiesSection";
import AlpacaCard from "@/components/AlpacaCard";

import AdminLoginNotice from "@/components/AdminLoginNotice";
import FloatingElements from "@/components/FloatingElements";
import GoogleReviews from "@/components/GoogleReviews";

import { apiSync } from "@/lib/api";
import { openNostiumWidget } from "@/lib/nostium";
import heroImage from "@/assets/hero-alpacas.webp";
import {
  Heart,
  Leaf,
  Users,
  Sparkles,
  Star,
  Sun,
  Cloud,
  Coffee,
  Camera,
  Music,
  Gift,
  Award,
  Smile,
  ThumbsUp,
  Home,
  TreeDeciduous,
  Flower2,
  PawPrint,
} from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const iconComponents = {
  heart: Heart,
  leaf: Leaf,
  users: Users,
  sparkles: Sparkles,
  star: Star,
  sun: Sun,
  cloud: Cloud,
  coffee: Coffee,
  camera: Camera,
  music: Music,
  gift: Gift,
  award: Award,
  smile: Smile,
  thumbsUp: ThumbsUp,
  home: Home,
  tree: TreeDeciduous,
  flower: Flower2,
  paw: PawPrint,
};

const getShuffled = <T,>(items: T[], limit: number) => {
  const shuffled = [...items].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, limit);
};

const Index = () => {
  const siteSettings = apiSync.getSiteSettings();
  const homepage = apiSync.getHomepageSettings();
  const allActivities = apiSync.getActivities();
  const allAlpacas = apiSync.getAlpacas();
  useScrollAnimation();

  const alpacas = useMemo(
    () => getShuffled(allAlpacas, 3),
    [allAlpacas]
  );

  // Default values while loading
  const hero = homepage?.hero || {
    badge: "Leukste van de Drechtsteden! 🌟",
    title: "Alpacaboerderij Dordrecht",
    subtitle:
      "Alleen op reservering · Geniet van een unieke ervaring tussen onze vriendelijke alpaca's in een prachtige groene omgeving midden in Dordrecht.",
    image: heroImage,
  };

  const features = homepage?.features || [
    {
      id: 1,
      title: "Diervriendelijk",
      description: "Onze alpaca's hebben alle ruimte en rust.",
      icon: "heart" as const,
    },
    { id: 2, title: "Mooie Omgeving", description: "Volledig afgeschermd van de stad.", icon: "leaf" as const },
    {
      id: 3,
      title: "Lokale Samenwerkingen",
      description: "Verse biologische thee en lekkernijen.",
      icon: "users" as const,
    },
    { id: 4, title: "Onvergetelijk", description: "Een unieke ervaring!", icon: "sparkles" as const },
  ];

  const reservation = homepage?.reservation || {
    title: "Het leukste uitje voor jong & oud",
    description: "Kom snel een keer langs! Alleen op reservering.",
    buttonText: "Plan je bezoek",
    buttonUrl: "/bezoek",
  };

  const aboutPreview = homepage?.aboutPreview || {
    title: "Even voorstellen",
    text: "Welkom bij Alpacaboerderij Dordrecht. Sinds 2024 geven we bezoekers een fijne break van het drukke leven, midden in een groen weiland tussen onze alpaca's.",
    buttonText: "Lees meer",
  };
  const activities = allActivities;
  const showMoreActivitiesButton = activities.length > 2 && siteSettings?.homepageActivitiesMoreButtonEnabled !== false;
  const homepageActivities = useMemo(() => {
    const shouldRandomizeActivities = activities.length > 2 && siteSettings?.homepageActivitiesRandomizedEnabled === true;
    if (!shouldRandomizeActivities) return activities;
    return getShuffled(activities, 2);
  }, [activities, siteSettings?.homepageActivitiesRandomizedEnabled]);

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead
        title="Alpacaboerderij Dordrecht - Het leukste uitje voor jong & oud"
        description="Geniet van een unieke ervaring tussen onze vriendelijke alpaca's. Alpacapicknick en high tea in een prachtige groene omgeving midden in Dordrecht. Alleen op reservering."
        canonical="/"
      />

      <Header />

      <AdminLoginNotice />

      <main>
        {/* Hero Section */}
        <section
          className="relative overflow-hidden bg-gradient-to-br from-[rgba(49,56,129,0.1)] via-[rgba(133,214,214,0.2)] to-white"
          aria-labelledby="hero-heading"
        >
          <FloatingElements />
          <div
            className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzAwMCIgc3Ryb2tlLW9wYWNpdHk9IjAuMDMiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-40"
            aria-hidden="true"
          ></div>

          <div className="container relative py-20 md:py-32">
            <div className="grid gap-12 lg:grid-cols-2 items-center">
              <div className="space-y-6 animate-fade-in motion-reduce:animate-none">
                <p className="inline-block rounded-full bg-[rgba(255,214,102,0.2)] px-4 py-2 text-sm font-medium text-[#673F21]">
                  {hero.badge}
                </p>
                <h1 id="hero-heading" className="text-4xl md:text-6xl font-bold leading-tight">
                  {hero.title.includes("Dordrecht") ? (
                    <>
                      {hero.title.replace("Dordrecht", "")}
                      <span className="gradient-text">Dordrecht</span>
                    </>
                  ) : (
                    hero.title
                  )}
                </h1>
                <p className="text-xl text-muted-foreground max-w-lg whitespace-pre-line">{hero.subtitle}</p>
                <nav className="flex flex-wrap gap-4" aria-label="Primaire acties">
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-lg px-8"
                    onClick={openNostiumWidget}
                  >
                    Plan je bezoek
                  </Button>
                  <Button size="lg" variant="outline" className="text-lg px-8" asChild>
                    <Link to="/alpacas">Ontmoet de Alpaca's</Link>
                  </Button>
                </nav>
              </div>

              <figure className="relative">
                <div
                  className="absolute inset-0 bg-gradient-to-br from-[rgba(49,56,129,0.2)] to-[rgba(255,214,102,0.2)] rounded-full blur-3xl"
                  aria-hidden="true"
                ></div>
                <img
                  src={hero.image}
                  alt="Groep alpaca's grazen vredig in een groen weiland bij Alpacaboerderij Dordrecht"
                  className="relative rounded-3xl shadow-2xl animate-float motion-reduce:animate-none"
                  width="600"
                  height="400"
                  loading="eager"
                  fetchPriority="high"
                />
              </figure>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-muted/30" aria-labelledby="features-heading">
          <div className="container">
            <header className="text-center mb-12 scroll-fade-in">
              <h2 id="features-heading" className="text-3xl md:text-4xl font-bold mb-4">
                Waarom Alpacaboerderij Dordrecht?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Een unieke ervaring die je niet snel zult vergeten
              </p>
            </header>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {features.map((feature, index) => {
                const IconComponent = iconComponents[feature.icon];
                return (
                  <Card
                    key={feature.id}
                    className="p-6 text-center hover-lift scroll-fade-in"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary mb-4">
                      <IconComponent className="h-8 w-8" aria-hidden="true" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground whitespace-pre-line">{feature.description}</p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <HomepageActivitiesSection activities={homepageActivities} showMoreButton={showMoreActivitiesButton} />

        {/* Featured Alpacas */}
        <section className="py-20 bg-gradient-to-b from-background to-muted/30" aria-labelledby="alpacas-heading">
          <div className="container">
            <header className="text-center mb-12 scroll-fade-in">
              <h2 id="alpacas-heading" className="text-3xl md:text-4xl font-bold mb-4">
                Maak kennis met onze alpaca's
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
                Ze zijn erg nieuwsgierig en verwelkomen jullie graag!
              </p>
            </header>

            <div className="grid gap-8 md:grid-cols-3 mb-8">
              {alpacas.map((alpaca, index) => (
                <article key={alpaca.id} className="scroll-fade-in" style={{ animationDelay: `${index * 0.2}s` }}>
                  <AlpacaCard alpaca={alpaca} />
                </article>
              ))}
            </div>

            <div className="text-center mx-[6px] pt-[20px]">
              <Button size="lg" variant="outline" className="text-lg px-8" asChild>
                <Link to="/alpacas">Ontmoet alle alpaca's</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Google Reviews */}
        <GoogleReviews />

        <section className="py-20 bg-muted/30" aria-labelledby="about-preview-heading">
          <div className="container max-w-5xl">
            <Card className="p-8 md:p-12 bg-gradient-to-br from-background via-background to-primary/5 shadow-lg border-primary/10">
              <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
                <div className="space-y-4">
                  <h2 id="about-preview-heading" className="text-3xl md:text-4xl font-bold">
                    {aboutPreview.title}
                  </h2>
                  <p className="text-lg text-muted-foreground leading-8 max-w-3xl whitespace-pre-line">{aboutPreview.text}</p>
                </div>
                <div>
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-lg px-8"
                    asChild
                  >
                    <Link to="/over-ons">{aboutPreview.buttonText}</Link>
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 to-accent/10" aria-labelledby="cta-heading">
          <div className="container text-center scroll-fade-in">
            <h2 id="cta-heading" className="text-3xl md:text-5xl font-bold mb-6">
              {reservation.title}
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto whitespace-pre-line">{reservation.description}</p>
            <Button
              size="lg"
              className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-lg px-12"
              onClick={openNostiumWidget}
            >
              {reservation.buttonText}
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
